/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef } from 'react';

import config from './config';
import { useIdClaim, useProfile, useTenants, useSecurity } from './hooks';
import { getImpersonateId, hasImpersonateAccess } from './shared/helper';

export const KIWI_NAV_STATE = {
  DONE: 'done',
  FAILED: 'failed',
  IDLE: 'idle',
  LOADING: 'loading',
};

export function KiwiNavbar() {
  const webComponentRef = useRef();
  const [status, setStatus] = React.useState(KIWI_NAV_STATE.IDLE);

  const { profile } = useProfile();

  const tenants = useTenants();

  const idClaim = useIdClaim();
  const impersonateId = getImpersonateId();
  const { browserSecurity } = useSecurity();

  const webComponentURL = config.RSS_WC_URL.includes('localhost')
    ? `${config.RSS_WC_URL}/rss-nav-bar.mjs`
    : `${config.RSS_WC_URL}/core/rss-nav-bar.mjs`;

  const setProps = useCallback(
    (appContext = null) => {
      if (webComponentRef.current) {
        const campus = tenants.getCampus(profile?.campusCode);
        webComponentRef.current.props = {
          appContext,
          user: {
            isSpoofed: Boolean(impersonateId),
            id: profile?.userId,
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            email: profile?.email,
            campus: {
              code: campus?.campusCode,
              name: campus?.displayName || campus?.shortName || campus?.name,
              uri: campus?.uri,
            },
            rss: hasImpersonateAccess(idClaim),
          },
          logout: () => browserSecurity.logout('/'),
          onRouteChange: (route) => {
            // All navbar route changes navigate outside of this client's router
            // Ex: /action-items, /workspace, /admin-tools, /profile, /developer/spoofing, etc
            window.location.href = route;
          },
        };
      }
    },
    [profile, idClaim],
  );

  useEffect(() => {
    async function load() {
      setStatus(KIWI_NAV_STATE.LOADING);

      try {
        await import(/* webpackIgnore: true */ webComponentURL);
        const newStatus = KIWI_NAV_STATE.DONE;
        setStatus(newStatus);
      } catch (err) {
        const newStatus = KIWI_NAV_STATE.FAILED;
        setStatus(newStatus);
      }
    }

    if (status === KIWI_NAV_STATE.IDLE) {
      load();
    }
  }, [status]);

  useEffect(() => {
    const isTracker = window.location.href.includes('tracker');
    const isChemicalInventory = window.location.href.includes('inventory/chemical');
    const isPPEInventory = !isChemicalInventory && window.location.href.includes('inventory');
    let appContext;
    if (isTracker) {
      appContext = {
        name: 'Tracker',
        appLinks: [
          { name: 'My Trackables', href: '/tracker' },
          { name: 'Group Trackables', href: '/tracker/group-trackables' },
        ],
      };
    } else if (isChemicalInventory) {
      const pathname = window.location.href.split('/');
      const inventoryIdIndex = pathname.indexOf('chemical');
      const inventoryId = pathname[inventoryIdIndex + 1];

      const appLinks = [{ name: 'Chemical Inventory', href: `/chemicals?inventoryId=${inventoryId}` }];

      if (inventoryIdIndex !== -1 && inventoryId !== 'stockroom') {
        if (profile?.features?.chemical?.SOP?.value) {
          appLinks.push(
            { name: 'SOP', href: `/sop/inventory/${inventoryId}`, external: true },
            { name: 'SOP Requirements', href: `/inventory/chemical/${inventoryId}/sops` },
          );
        }
        appLinks.push({ name: 'Inventory Activity', href: `/inventory/chemical/${inventoryId}/history` });
      } else if (profile?.features?.chemical?.SOP?.value) {
        appLinks.push({ name: 'SOP', href: '/sop' });
      }
      appContext = {
        name: 'Inventory',
        appLinks,
      };
    } else if (isPPEInventory) {
      const pathname = window.location.href.split('/');
      const inventoryIdIndex = pathname.indexOf('inventory');
      const inventoryId = pathname[inventoryIdIndex + 1];

      const appLinks = [];
      if (/[0-9a-f]{24}/.test(inventoryId)) {
        appLinks.push(
          { name: 'Manage Inventory', href: `/inventory/${inventoryId}` },
          { name: 'Manage Inventory by User', href: `/inventory/${inventoryId}/member` },
          { name: 'Search Inventory', href: `/inventory/${inventoryId}/search` },
          { name: 'Inventory Owners', href: `/inventory/${inventoryId}/access` },
        );
      }
      appLinks.push(
        { name: 'Laundry Locations', href: '/location-area/laundry' },
        { name: 'My Items', href: '/inventory/items' },
      );

      appContext = {
        name: 'Inventory',
        appLinks,
      };
    }

    if (appContext && status === KIWI_NAV_STATE.DONE) {
      setProps(appContext);
    }
  }, [profile, window.location.href, status]);

  if (status !== KIWI_NAV_STATE.DONE) {
    return null;
  }

  return (
    <rss-nav-bar
      ref={webComponentRef}
      ld-client-id={config.LD_CLIENT_ID}
      branding-url={config.BRANDING_GRAPH_URL}
      relationship-url={config.RELATIONSHIP_GRAPH_URL}
    />
  );
}
